import React, { useRef, useEffect, useState } from 'react';
import { FlourishAnimationData } from '../types';
import { Language } from '../data/translations';
import Card from './Card';

interface FlourishCardProps {
    animation: FlourishAnimationData;
    onEnd: (id: string) => void;
    language: Language;
}

/**
 * FlourishCard - Precise 2-phase flourish animation.
 * 
 * Logic:
 * - Uses a full-screen fixed container to handle absolute screen coordinates easily.
 * - Phase 1: Card flies from startRect to off-screen TOP with random X, curve, and zoom IN.
 * - Phase 2: Card flies from off-screen BOTTOM (random X) back to startRect, zoom OUT.
 * - Uses Bezier curves for smooth trajectory.
 * - Matches rotation speed and exact start/end positions for seamless transition.
 */
const FlourishCard: React.FC<FlourishCardProps> = ({ animation, onEnd, language }) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Physics parameters calculated once
    const [params] = useState(() => {
        // 1. Speed: 300 to 700 px/s
        const speed = 300 + Math.random() * 400;

        // 2. Scale: 101% to 600% (1% to 500% increase)
        const maxScale = 1.01 + Math.random() * 5.0;

        // 3. Curve bend: 0 to 10 degrees (simulated by control point offset)
        const curveIntensity = Math.random() * 0.5 * window.innerWidth; // Variance for control point
        const curveDirection = Math.random() > 0.5 ? 1 : -1;

        // 4. Phase 1 Target (Front/Up) - Random X, Offscreen Y
        // "Choosing random direction on the horizontal axis in front"
        // We target a point well above the screen to ensure it flies "away"
        const targetX1 = Math.random() * window.innerWidth;
        const targetY1 = -500; // Well off-screen top

        // 5. Phase 2 Start (Bottom) - "Choosing random direction on the bottom screen x horizontal axis"
        const startX2 = Math.random() * window.innerWidth;
        const startY2 = window.innerHeight + 500; // Well off-screen bottom

        return {
            speed,
            maxScale,
            curveOffset: curveIntensity * curveDirection,
            targetX1,
            targetY1,
            startX2,
            startY2,
            rotationSpeed: animation.rotationSpeed * 2 // "Double rotation speed"
        };
    });

    const startRect = animation.startRect;

    // Initial center position (exact screen coordinates)
    // We use the center for trajectory calculations to be precise
    const startCx = startRect.left + startRect.width / 2;
    const startCy = startRect.top + startRect.height / 2;

    // Calculate distances and durations
    const dist1 = Math.hypot(params.targetX1 - startCx, params.targetY1 - startCy);
    const duration1 = (dist1 / params.speed) * 1000;

    const dist2 = Math.hypot(startCx - params.startX2, startCy - params.startY2);
    const duration2 = (dist2 / params.speed) * 1000;

    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;

        // --- Phase 1: Fly OUT (Start -> Offscreen Top) ---
        const steps = 60; // Smooth sampling
        const keyframes1 = [];
        const rotSpeedDegPerMs = (params.rotationSpeed * 15 * 2) / 500; // steps * 15deg / 500ms * 2

        for (let i = 0; i <= steps; i++) {
            const t = i / steps; // 0 to 1

            // Quadratic Bezier
            // P0: Start Center
            // P1: Control Point (Midpoint + Curve Offset)
            // P2: Target Center

            const p0x = startCx;
            const p0y = startCy;
            const p2x = params.targetX1;
            const p2y = params.targetY1;

            // Control point: halfway + random horiz offset for curve
            const p1x = (p0x + p2x) / 2 + params.curveOffset;
            const p1y = (p0y + p2y) / 2; // Linear vertical progress for control? Or arc it?
            // Let's keep control Y halfway to avoid weird looping

            const x = Math.pow(1 - t, 2) * p0x + 2 * (1 - t) * t * p1x + Math.pow(t, 2) * p2x;
            const y = Math.pow(1 - t, 2) * p0y + 2 * (1 - t) * t * p1y + Math.pow(t, 2) * p2y;

            // Scale: 1 -> Max
            const scale = 1 + (params.maxScale - 1) * t; // Linear zoom as it flies away/closer? 
            // User said "zooming in size" as it flies forward.

            // Rotation: Start Rotation + Speed * Time
            // duration of this frame = t * duration1
            const currentRot = animation.rotation + (rotSpeedDegPerMs * (t * duration1));

            // Convert Center X,Y back to Top,Left translation for the element (which is sized startRect.width/height)
            const translateX = x - startRect.width / 2;
            const translateY = y - startRect.height / 2;

            keyframes1.push({
                transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale}) rotate(${currentRot}deg)`,
                opacity: 1, // Stay visible
                offset: t
            });
        }

        // Add a final frame to ensure it stays offscreen/hidden at end of phase 1
        keyframes1[keyframes1.length - 1].opacity = 0;

        const anim1 = el.animate(keyframes1, {
            duration: duration1,
            fill: 'forwards',
            easing: 'linear' // Logic is in keyframes
        });

        anim1.onfinish = () => {
            // --- Phase 2: Fly BACK (Offscreen Bottom -> Start) ---
            // "switch on fake image... smooth switch"
            // We reuse the same element, just jump its position

            const keyframes2 = [];
            const phase1EndRot = animation.rotation + (rotSpeedDegPerMs * duration1);

            for (let i = 0; i <= steps; i++) {
                const t = i / steps; // 0 to 1

                // P0: Offscreen Bottom (Random X)
                // P2: ORIGINAL Start Center

                const p0x = params.startX2;
                const p0y = params.startY2;
                const p2x = startCx;
                const p2y = startCy;

                // "use same curve parameter" - we'll infer a similar curve shape
                const p1x = (p0x + p2x) / 2 + params.curveOffset;
                const p1y = (p0y + p2y) / 2;

                const x = Math.pow(1 - t, 2) * p0x + 2 * (1 - t) * t * p1x + Math.pow(t, 2) * p2x;
                const y = Math.pow(1 - t, 2) * p0y + 2 * (1 - t) * t * p1y + Math.pow(t, 2) * p2y;

                // Scale: Max -> 1
                const scale = params.maxScale - ((params.maxScale - 1) * t);

                // Rotation continues
                const currentRot = phase1EndRot + (rotSpeedDegPerMs * (t * duration2));

                const translateX = x - startRect.width / 2;
                const translateY = y - startRect.height / 2;

                keyframes2.push({
                    transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scale}) rotate(${currentRot}deg)`,
                    opacity: 1,
                    offset: t
                });
            }

            // Ensure exact final frame matches original state (scale 1, exact pos)
            const finalFrame = keyframes2[keyframes2.length - 1];
            // We want opacity 1 at the end, so when we unmount, the original card (which becomes visible) matches.
            // But original card might have different rotation (user stopped spinning).
            // The prompt says: "reach the spot in exactly position it should be located there."
            // We will end exactly at startRect.

            const anim2 = el.animate(keyframes2, {
                duration: duration2,
                fill: 'forwards',
                easing: 'linear'
            });

            anim2.onfinish = () => {
                onEnd(animation.id);
            };
        };

        return () => {
            anim1.cancel();
        };

    }, []);

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            pointerEvents: 'none',
            zIndex: 200, // Above everything
            perspective: '1000px',
        }}>
            <div
                ref={containerRef}
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: startRect.width,
                    height: startRect.height,
                    // Initial state before animation starts (avoids flicker)
                    transform: `translate3d(${startRect.left}px, ${startRect.top}px, 0) rotate(${animation.rotation}deg)`,
                    willChange: 'transform'
                }}
            >
                <Card card={animation.card} rotation={0} language={language} /> {/* Rotation handled by container */}
            </div>
        </div>
    );
};

export default FlourishCard;
